import { Drawer, Input, message, Select, Space } from 'antd';
import React, { useEffect, useMemo } from 'react';
import { Button, Form } from 'antd';
import { useInternalClientServiceGetInternalClient, useInternalTagServiceGetInternalTag, useInternalTemplateTypeServiceGetInternalTemplateType, useSceneServiceGetScene, useTemplateServicePutTemplate } from '../../../openapi/queries';
import { TemplateEditDTO } from '../../../openapi/requests';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';


const EditTemplate: React.FC<{ template?: TemplateEditDTO, isOpen: boolean, onClose: () => void }> = ({ template, isOpen, onClose }) => {
    const {data: clients} = useInternalClientServiceGetInternalClient({}, undefined, {enabled: isOpen});
    const {data: templateTypes} = useInternalTemplateTypeServiceGetInternalTemplateType({}, undefined, {enabled: isOpen});
    const {data: scenes} = useSceneServiceGetScene({}, undefined, {enabled: isOpen});
    const { data: alltags } = useInternalTagServiceGetInternalTag({}, undefined, { enabled: isOpen });
    const [form] = Form.useForm<TemplateEditDTO>();
    const [messageApi, contextHolder] = message.useMessage();
    const {mutateAsync, isPending } = useTemplateServicePutTemplate();

    const tags = useMemo(() => {
        if (alltags) {
            const tags = Array.from(new Set(alltags.value.map(e => e.value)));
            tags.sort();
            return tags;
        }
        return [];
    }, [alltags]);

    const onSubmit = () => {
        form.submit();
    }

    const onFinish = () => {
        if (template) {
            const templateData = form.getFieldsValue(true) as TemplateEditDTO;

            mutateAsync({key: template.id, requestBody: templateData})
                .then(() => {
                    messageApi.success("Template updated");
                    onClose();
                }).catch(reason => {
                    messageApi.error(JSON.stringify(reason));
                });
        }
    };

    useEffect(() => {
        if (template) {          
            form.setFieldsValue(template);
        }
    }, [template, form])

    return (
        <>
            {contextHolder}

            <Drawer
                title={`Edit ${template?.title}`}
                width={720}
                onClose={onClose}
                open={isOpen}
                bodyStyle={{ paddingBottom: 80 }}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={onSubmit} type="primary" loading={isPending}>
                            Submit
                        </Button>
                    </Space>
                }
            >
                <Form
                    name="basic"
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}>

                    <Form.Item rules={[{required: true}]} name='clientId' label="Client">
                        <Select options={clients?.value.map(e => ({ label: e.name, value: e.id }))} />
                    </Form.Item>

                    <Form.Item rules={[{required: true}]} name='title' label="Title">
                        <Input />
                    </Form.Item>

                    <Form.Item rules={[{required: true}]} name='templatetypeId' label="Template type">
                        <Select options={templateTypes?.value.map(e => ({ label: e.name, value: e.id }))} />
                    </Form.Item>

                    <Form.Item name='sceneId' label="Scene">
                        <Select allowClear options={scenes?.value.map(e => ({ label: e.title, value: e.id }))} />
                    </Form.Item>

                    <Form.Item name='editorModule' label="Editor module">
                        <Input />
                    </Form.Item>

                    <Form.Item name='tags' label="Tags">
                        <Select mode='tags' options={tags.map(e => ({ label: e, value: e }))} />
                    </Form.Item>

                    <Form.Item label='Metadata'>
                        <Form.List name="metadata">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex' }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'name']}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="name" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'value']}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="value" />
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Add metadata
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>
                </Form>
            </Drawer>
        </>

    );
};

export default EditTemplate;